import {
  GetVideoSuggestedUserMentionsOptions,
  GetVideoSuggestedUserMentionsResult,
  getVideoSuggestedUserMentions,
  GetVideoSuggestedUserMentionsFields,
} from '../fetchers/video/suggested_user_mentions';
import useSWR, { SWRConfiguration, useSWRConfig } from 'swr';
import { assignMswData, useInternalState, serializeQuery } from '../hooks/internalState';
import { useCallback } from 'react';
import useSWRInfinite, { SWRInfiniteConfiguration } from 'swr/infinite';
import { useGctlConfig } from '../hooks/GctlProvider';

/**
 * DO NOT MODIFY
 * Auto-generated by gctl-plugin-vimeo-hooks
 */

type UseGetVideoSuggestedUserMentionsOptions<Fields extends GetVideoSuggestedUserMentionsFields> =
  GetVideoSuggestedUserMentionsOptions<Fields>;

/**
 * Get suggested users for mentions in comments
 * This method returns all the suggested users who can be mentioned in the comments of the specified video based on the user's search query. Please note that the comments used for generating the suggestions differ based on the source filter.
 */

export function useGetVideoSuggestedUserMentions<
  Fields extends GetVideoSuggestedUserMentionsFields,
>(
  getOptions:
    | UseGetVideoSuggestedUserMentionsOptions<Fields>
    | (() => UseGetVideoSuggestedUserMentionsOptions<Fields> | null),
  config?: SWRConfiguration<GetVideoSuggestedUserMentionsResult<Fields>>,
) {
  const options = typeof getOptions === 'function' ? getOptions() : getOptions;

  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  return useSWR(
    options
      ? `/videos/${options.where.videoId}/suggested_user_mentions${serializeQuery(options)}`
      : () => null,
    options
      ? () => {
          return getVideoSuggestedUserMentions({
            ...options,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
            baseUrl,
          });
        }
      : null,
    config,
  );
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetVideoSuggestedUserMentions, {
    endpoint: '/videos/:videoId/suggested_user_mentions',
    method: 'GET',
  });
}

/**
 * Get suggested users for mentions in comments
 * This method returns all the suggested users who can be mentioned in the comments of the specified video based on the user's search query. Please note that the comments used for generating the suggestions differ based on the source filter.
 */

export function useGetVideoSuggestedUserMentionsLazy<
  Fields extends GetVideoSuggestedUserMentionsFields,
>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<
    GetVideoSuggestedUserMentionsResult<Fields> | undefined
  >();

  let callback = useCallback(
    async (options: UseGetVideoSuggestedUserMentionsOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/videos/${options.where.videoId}/suggested_user_mentions${serializeQuery(options)}`,
          getVideoSuggestedUserMentions({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [callback, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetVideoSuggestedUserMentionsLazy, {
    endpoint: '/videos/:videoId/suggested_user_mentions',
    method: 'GET',
  });
}

type UseGetVideoSuggestedUserMentionsOptionsInfinite<
  Fields extends GetVideoSuggestedUserMentionsFields,
> = GetVideoSuggestedUserMentionsOptions<Fields>;

/**
 * Get suggested users for mentions in comments
 * This method returns all the suggested users who can be mentioned in the comments of the specified video based on the user's search query. Please note that the comments used for generating the suggestions differ based on the source filter.
 */

export function useGetVideoSuggestedUserMentionsInfinite<
  Fields extends GetVideoSuggestedUserMentionsFields,
>(
  getOptions:
    | UseGetVideoSuggestedUserMentionsOptionsInfinite<Fields>
    | (() => UseGetVideoSuggestedUserMentionsOptionsInfinite<Fields> | null),
  config?: SWRInfiniteConfiguration<GetVideoSuggestedUserMentionsResult<Fields>>,
) {
  const options = typeof getOptions === 'function' ? getOptions() : getOptions;

  let getKey = (
    pageIndex: number,
    previousPageData: GetVideoSuggestedUserMentionsResult<Fields> | null,
  ) => {
    if (options === null) {
      return null;
    }
    if (previousPageData && !previousPageData.paging.next) {
      return null;
    }
    let { perPage = 25, page: _page, ...rest } = options.query ?? {};
    let select = options.select.join(',');
    let queryString = Object.entries(rest ?? {})
      .filter(([, value]) => typeof value !== 'undefined')
      .map(([key, value]) => `${key}=${value}`)
      .join('&');
    return [
      `/videos/${options.where.videoId}/suggested_user_mentions?page=${
        pageIndex + 1
      }&perPage=${perPage}&fields=${select}&${queryString}`,
      pageIndex,
    ];
  };
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  return useSWRInfinite(
    getKey,
    options !== null
      ? ([_uri, pageIndex]: [string, number]) => {
          return getVideoSuggestedUserMentions({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
            query: { ...options.query, page: pageIndex + 1 },
          });
        }
      : null,
    config,
  );
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetVideoSuggestedUserMentionsInfinite, {
    endpoint: '/videos/:videoId/suggested_user_mentions',
    method: 'GET',
  });
}
